import * as moment from 'moment';
import { Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { onTimeChange } from 'src/app/utils/utils.common';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BillService } from 'src/app/services/core/services/bills/bill.service';
import { VendorService } from 'src/app/services/core/services/vendors/vendor.service';
import { SharedDataService } from 'src/app/services/app/shared-service/shared-data.service';
import { ProductService } from 'src/app/services/core/services/product-services/product.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyService } from 'src/app/services/core/company/company.service';
import { sweetAlert } from 'src/app/utils/swal';
import { getCompanyEmail, getPurchasableProductItems, isPurchasableProductItem } from 'src/app/utils/common.utils';
@UntilDestroy()

@Component({
  selector: 'app-add-bill',
  templateUrl: './add-bill.component.html',
  styleUrls: ['./add-bill.component.css']
})
export class AddBillComponent implements OnInit {

  billForm!: FormGroup;
  submitted = false;
  lineItemSubmitted = false;
  billId: any;
  user: any;
  vendors: any;
  productItems: any;
  selectedCompany: any = '';
  companies: any;
  previousVendorId = '';
  hasPurchasableItems = true;

  constructor(
    private companyService: CompanyService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private productService: ProductService,
    private vendorService: VendorService,
    private billService: BillService,
    private sharedDataService: SharedDataService,
    private fb: FormBuilder) { }

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(untilDestroyed(this)).subscribe((params) => {
        this.billId = params['id'];
        if (this.billId) {
          this.getBillById();
        }
      });

    this.billForm = this.fb.group({
      VendorRef: this.fb.group({
        name: [''],
        value: ['', Validators.required]
      }),
      DueDate: [this.billId ? '' : moment().add(1, 'month').toISOString()],
      BillEmail: this.fb.group({
        Address: ['', [Validators.required, Validators.email]]
      }),
      Line: this.fb.array([
        this.createLineItem()
      ]),
      reference: this.fb.group({
        name: [''],
        id: ['']
      })
    });
    this.getUser();
    this.getCompanies();
  }

  get companyId() {
    return this.selectedCompany?.id || this.selectedCompany || '';
  }

  get form() {
    return this.billForm.controls;
  }

  get lineItems() {
    return this.billForm.get('Line') as FormArray;
  }

  get BillEmail() {
    return this.billForm.get('BillEmail') as FormGroup;
  }

  firstProduct() {
    return (this.productItems || []).find((item: any) => item?.Id);
  }

  isBillableItemId(itemId: any) {
    if (!itemId) return false;
    if (!this.productItems) return true;
    return this.productItems.some((item: any) => String(item.Id) === String(itemId) && isPurchasableProductItem(item));
  }

  createLineItem(line: any = {}): FormGroup {
    const product = this.firstProduct();
    const itemDetail = line.SalesItemLineDetail || line.ItemBasedExpenseLineDetail || {};
    const storedItemId = this.isBillableItemId(itemDetail.ItemRef?.value) ? itemDetail.ItemRef?.value : '';
    const amount = line.Amount ?? (product?.UnitPrice || 0);
    const detailType = line.DetailType === 'ItemBasedExpenseLineDetail' || line.DetailType === 'AccountBasedExpenseLineDetail'
      ? 'SalesItemLineDetail'
      : (line.DetailType || 'SalesItemLineDetail');
    return this.fb.group({
      DetailType: [detailType, Validators.required],
      Amount: [amount, [Validators.required, Validators.min(1)]],
      Description: [line.Description || '', line.DetailType ? [] : Validators.required],
      SalesItemLineDetail: this.fb.group({
        ItemRef: this.fb.group({
          value: [storedItemId || product?.Id || '', Validators.required],
        }),
        ServiceDate: [itemDetail.ServiceDate || new Date().toISOString(), Validators.required],
        Qty: [itemDetail.Qty || 1, Validators.required],
        Sku: [{ value: itemDetail.Sku || product?.Sku || '0', disabled: true }],
        UnitPrice: [itemDetail.UnitPrice ?? amount],
      })
    });
  }

  addLineItem() {
    if (this.lineItems.invalid) {
      this.lineItemSubmitted = true;
      return;
    }
    this.lineItems.push(this.createLineItem());
  }

  removeLineItem(index: number) {
    this.lineItems.removeAt(index);
  }

  getUser() {
    this.sharedDataService.getUser().pipe(untilDestroyed(this)).subscribe((data) => {
      if (data) {
        this.user = data;
      }
    });
  }

  getBillById() {
    this.billService.getBillById(this.billId)
      .pipe(untilDestroyed(this)).subscribe((data: any) => {
        if (!data) return;
        this.selectedCompany = data.company?.id || data.company;
        if (this.selectedCompany) {
          this.getAllVendors();
          this.getProductItems();
        }
        this.billForm.patchValue(data);
        this.previousVendorId = data.VendorRef?.value || '';
        this.applyBillLines(data.Line);
      });
  }

  applyBillLines(lines: any) {
    this.lineItems.clear();
    (lines || []).forEach((line: any) => {
      if (line.DetailType !== 'SubTotalLineDetail') {
        this.lineItems.push(this.createLineItem(line));
      }
    });
    if (!this.lineItems.length) {
      this.lineItems.push(this.createLineItem());
    }
  }

  applyDefaultLineProducts() {
    const product = this.firstProduct();
    if (!product) return;
    this.lineItems.controls.forEach((item) => {
      const itemRef = item.get('SalesItemLineDetail.ItemRef.value');
      if (!itemRef || (itemRef.value && this.isBillableItemId(itemRef.value))) return;
      const salesDetail = item.get('SalesItemLineDetail') as FormGroup;
      const amount = parseFloat(item.get('Amount')?.value) || 0;
      const rawPrice = salesDetail.get('UnitPrice')?.value;
      const unitPrice = rawPrice === '' || rawPrice == null ? (amount || parseFloat(product.UnitPrice) || 0) : parseFloat(rawPrice) || 0;
      itemRef.patchValue(product.Id);
      salesDetail.patchValue({ Sku: product.Sku || 0, UnitPrice: unitPrice });
      if (!amount) {
        item.get('Amount')?.patchValue((+salesDetail.get('Qty')?.value || 1) * (+product.UnitPrice || 0));
      }
    });
  }

  onSave() {
    this.submitted = true;
    this.lineItemSubmitted = true;
    if (this.billForm.invalid) return;

    const value = this.billForm.value;
    const vendorId = value.VendorRef.value;
    const selectedVendor = this.vendors?.find((v: any) => v.Id === vendorId);
    const vendorName = selectedVendor?.DisplayName || value.VendorRef.name;
    const payload = {
      ...value,
      TxnDate: value.TxnDate || new Date().toISOString(),
      VendorRef: vendorName ? { name: vendorName, value: vendorId } : { value: vendorId },
      company: selectedVendor?.company?.id || selectedVendor?.company || this.companyId,
      Line: value.Line || [],
    };

    this.billService.saveBill(this.billId, payload)
      .pipe(untilDestroyed(this)).subscribe({
        next: (data: any) => {
          if (!data) return;

          this.router.navigateByUrl('/bills');
        },
        error: (err) => {
          sweetAlert('Error', err, 'error', 'Ok');
        }
      });
  }

  onDateChange(controlName: AbstractControl<any, any> | null) {
    onTimeChange(controlName);
  }

  getAllVendors() {
    this.vendors = null;
    this.vendorService.getVendors({ limit: 9999, company: this.companyId })
      .pipe(untilDestroyed(this)).subscribe((data: any) => {
        if (data) {
          this.vendors = (data.results || []).filter((vendor: any) => vendor.Id);
        }
      });
  }

  getProductItems() {
    this.productService.getProductServices({ limit: 9999, company: this.companyId })
      .pipe(untilDestroyed(this)).subscribe((data: any) => {
        if (data) {
          this.productItems = getPurchasableProductItems(data.results);
          this.hasPurchasableItems = !!this.productItems.length;
          this.applyDefaultLineProducts();
        }
      });
  }

  getCompanies() {
    this.companyService.getCompanies({ status: 'created' })
      .pipe(untilDestroyed(this)).subscribe((data: any) => {
        if (data) {
          this.companies = data.results;
        }
      });
  }

  onCompanyChange() {
    if (this.selectedCompany) {
      this.getAllVendors();
      this.getProductItems();
      this.applySelectedCompanyEmail();
    } else {
      this.BillEmail.get('Address')?.patchValue('');
    }
  }

  applySelectedCompanyEmail() {
    const company = this.companies?.find((item: any) => (item.id || item._id) === this.companyId);
    this.BillEmail.get('Address')?.patchValue(getCompanyEmail(company));
  }

  onChangeVendor(event: Event) {
    const vendorId = (event.target as HTMLSelectElement).value;
    const selected = this.vendors?.find((vendor: any) => vendorId == vendor.Id);
    if (!selected) return;
    this.billForm.patchValue({
      VendorRef: { name: selected.DisplayName, value: selected.Id },
      reference: {
        name: selected.reference?.name || selected.DisplayName || '',
        id: selected.reference?.id || selected.Id || ''
      },
      BillEmail: {
        Address: selected?.PrimaryEmailAddr?.Address || getCompanyEmail(
          this.companies?.find((item: any) => (item.id || item._id) === this.companyId)
        )
      }
    });
    this.previousVendorId = vendorId;
  }

  onServiceSelect(event: Event, index: number) {
    const selectedProduct = this.productItems?.find((p: any) => p.Id === (event.target as HTMLSelectElement).value);
    if (!selectedProduct) return;
    const lineGroup = this.lineItems.at(index) as FormGroup;
    const salesDetail = lineGroup.get('SalesItemLineDetail') as FormGroup;
    const unitPrice = parseFloat(selectedProduct.UnitPrice) || 0;
    salesDetail.patchValue({ Sku: selectedProduct.Sku || 0, UnitPrice: unitPrice });
    lineGroup.get('Amount')?.patchValue((parseFloat(salesDetail.get('Qty')?.value) || 1) * unitPrice);
  }

  onQtyChange(index: number) {
    const lineGroup = this.lineItems.at(index) as FormGroup;
    const salesDetail = lineGroup.get('SalesItemLineDetail') as FormGroup;
    lineGroup.get('Amount')?.patchValue((parseFloat(salesDetail.get('Qty')?.value) || 0) * (parseFloat(salesDetail.get('UnitPrice')?.value) || 0));
  }

}
